import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const QUIZZES_COL = "baptism_quizzes";

const SCORES_COL = "baptism_leaderboard";
const G = "#c9a55a";
const NAVY = "#12122a";
const ADMIN_PASS = "admin123";

const MEDAL = ["🥇", "🥈", "🥉"];
const MEDAL_BG = [
  "linear-gradient(135deg,#fef3c7,#fde68a)",
  "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
  "linear-gradient(135deg,#fef3c7,#fed7aa)",
];
const MEDAL_BORDER = ["#f59e0b", "#94a3b8", "#f97316"];

export default function BaptismLeaderboard() {
  const [players, setPlayers] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState("all");
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPass, setAdminPass] = useState("");
  const [adminUnlocked, setAdminUnlocked] = useState(() => localStorage.getItem("baptism_admin_token") === "true");
  const [adminError, setAdminError] = useState(false);
  const [customName, setCustomName] = useState("");
  const [stepSize, setStepSize] = useState(10);

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }
    const unsubScores = onSnapshot(
      query(collection(db, SCORES_COL), orderBy("score", "desc")),
      (snap) => setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubQuizzes = onSnapshot(
      query(collection(db, QUIZZES_COL), orderBy("order", "asc")),
      (snap) => setQuizzes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubScores(); unsubQuizzes(); };
  }, []);

  const filtered = activeQuiz === "all" ? players : players.filter((p) => p.quizId === activeQuiz);
  const ranked = [...filtered].sort(
    (a, b) => b.score - a.score || (a.addedAt?.seconds ?? 0) - (b.addedAt?.seconds ?? 0)
  );

  const addPlayer = async (name) => {
    if (!name.trim()) return;
    const already = players.find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
    if (already) return;
    await setDoc(doc(db, SCORES_COL, `manual_${Date.now()}`), {
      name: name.trim(),
      score: 0,
      source: "manual",
      addedAt: serverTimestamp(),
    });
    setCustomName("");
  };

  const adjust = async (id, delta) => {
    const player = players.find((p) => p.id === id);
    if (!player) return;
    await updateDoc(doc(db, SCORES_COL, id), { score: Math.max(0, player.score + delta) });
  };

  const setScore = async (id, value) => {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 0) return;
    await updateDoc(doc(db, SCORES_COL, id), { score: n });
  };

  const removePlayer = (id) => deleteDoc(doc(db, SCORES_COL, id));

  const handleAdminUnlock = () => {
    if (adminPass === ADMIN_PASS) { setAdminUnlocked(true); setAdminError(false); }
    else setAdminError(true);
  };

  return (
    <div
      style={{
        background: "linear-gradient(160deg,#0d0d1e 0%,#12122a 60%,#0a0a18 100%)",
        minHeight: "100vh",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p style={{ letterSpacing: "0.28em", color: `${G}90` }} className="text-xs uppercase mb-3">
            Munachimso Zoe Maxwell · Baptism Games
          </p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: G, lineHeight: 1.1 }} className="text-5xl sm:text-6xl font-bold mb-3">
            Leaderboard
          </h1>
          <p style={{ color: "#6b6b8a" }} className="text-sm italic mb-5">
            Live rankings · updates in real-time
          </p>
          <Link
            to="/baptism/quiz"
            style={{ background: `linear-gradient(135deg,${G},#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em" }}
            className="inline-block text-white font-semibold py-3 px-8 rounded-xl uppercase text-sm hover:opacity-90 transition-opacity shadow-lg"
          >
            🎯 Play a Quiz
          </Link>
          <div className="flex items-center justify-center gap-3 mt-8 max-w-xs mx-auto">
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}40)` }} />
            <span style={{ color: G }}>✦</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}40,transparent)` }} />
          </div>
        </div>

        {/* Quiz filter tabs */}
        {quizzes.length > 0 && (
          <div className="flex gap-2 flex-wrap justify-center mb-8">
            {[{ id: "all", title: "All", emoji: "✦" }, ...quizzes].map((q) => (
              <button
                key={q.id}
                onClick={() => setActiveQuiz(q.id)}
                style={{
                  border: `1px solid ${activeQuiz === q.id ? G : "rgba(255,255,255,0.15)"}`,
                  background: activeQuiz === q.id ? `${G}22` : "rgba(255,255,255,0.04)",
                  color: activeQuiz === q.id ? G : "#6b6b8a",
                }}
                className="text-sm px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5"
              >
                <span>{q.emoji}</span>
                <span>{q.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* Podium — top 3 */}
        {ranked.length >= 1 && (
          <div className="flex items-end justify-center gap-3 mb-10">
            {[ranked[1], ranked[0], ranked[2]].map((p, slot) => {
              if (!p) return <div key={slot} className="w-24" />;
              const realRank = ranked.indexOf(p);
              const heights = ["h-24", "h-32", "h-20"];
              return (
                <div key={p.id} className="flex flex-col items-center gap-2 w-28">
                  <span className="text-2xl">{MEDAL[realRank] ?? ""}</span>
                  <p style={{ color: "#e2e8f0", fontFamily: "'Playfair Display', serif" }} className="text-sm font-semibold text-center leading-tight">
                    {p.name}
                  </p>
                  <div
                    style={{ background: MEDAL_BG[realRank] ?? `${G}18`, border: `2px solid ${MEDAL_BORDER[realRank] ?? G}`, width: "100%" }}
                    className={`${heights[slot]} rounded-t-2xl flex items-center justify-center`}
                  >
                    <span
                      style={{ fontFamily: "'Playfair Display', serif", color: realRank === 0 ? "#92400e" : realRank === 1 ? "#475569" : "#9a3412" }}
                      className="text-2xl font-bold"
                    >
                      {p.score}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full rankings */}
        <div className="space-y-2 mb-12">
          {ranked.length === 0 ? (
            <div className="text-center py-16">
              <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-base">
                No scores yet. Be the first to play the quiz!
              </p>
            </div>
          ) : (
            ranked.map((p, i) => (
              <div
                key={p.id}
                style={{
                  background: i === 0
                    ? "linear-gradient(135deg,rgba(201,165,90,0.15),rgba(201,165,90,0.05))"
                    : "rgba(255,255,255,0.04)",
                  border: `1px solid ${i === 0 ? `${G}50` : "rgba(255,255,255,0.07)"}`,
                }}
                className="rounded-2xl px-5 py-4 flex items-center gap-4"
              >
                <div className="w-8 text-center shrink-0">
                  {i < 3 ? (
                    <span className="text-xl">{MEDAL[i]}</span>
                  ) : (
                    <span style={{ color: "#6b6b8a", fontFamily: "'Playfair Display', serif" }} className="text-sm font-semibold">
                      {i + 1}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p style={{ color: i === 0 ? G : "#e2e8f0", fontFamily: "'Playfair Display', serif", fontWeight: i === 0 ? 700 : 500 }} className="text-base">
                    {p.name}
                  </p>
                  {p.quizTitle && (
                    <span style={{ color: "#6b6b8a", letterSpacing: "0.06em" }} className="text-xs">
                      {p.quizEmoji} {p.quizTitle}
                    </span>
                  )}
                </div>

                <span style={{ color: i === 0 ? G : "#94a3b8", fontFamily: "'Playfair Display', serif" }} className="text-xl font-bold tabular-nums shrink-0">
                  {p.score} <span style={{ fontSize: "0.65rem", letterSpacing: "0.1em" }}>pts</span>
                </span>
              </div>
            ))
          )}
        </div>

        {adminUnlocked && (
          <div className="pb-4">
          {true && (
            <div className="mt-6 text-left">
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }} className="rounded-2xl p-6 space-y-6">
                <h3 style={{ color: G, fontFamily: "'Playfair Display', serif" }} className="text-lg font-semibold">
                  Admin · Manage Scores
                </h3>

                {/* Score step */}
                <div>
                  <p style={{ color: "#6b6b8a", letterSpacing: "0.1em" }} className="text-xs uppercase mb-2">Score step</p>
                  <div className="flex gap-2 flex-wrap">
                    {[1, 5, 10, 25].map((n) => (
                      <button
                        key={n}
                        onClick={() => setStepSize(n)}
                        style={{
                          border: `1px solid ${stepSize === n ? G : "rgba(255,255,255,0.15)"}`,
                          background: stepSize === n ? `${G}22` : "transparent",
                          color: stepSize === n ? G : "#6b6b8a",
                        }}
                        className="text-sm px-4 py-1.5 rounded-lg transition-all"
                      >
                        +{n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Add custom player */}
                <div>
                  <p style={{ color: "#6b6b8a", letterSpacing: "0.1em" }} className="text-xs uppercase mb-2">Add player manually</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Player name…"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addPlayer(customName)}
                      style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#e2e8f0", fontFamily: "'Cormorant Garamond', serif" }}
                      className="border rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none"
                    />
                    <button
                      onClick={() => addPlayer(customName)}
                      disabled={!customName.trim()}
                      style={{ background: G, color: NAVY }}
                      className="font-semibold text-sm px-4 py-2 rounded-xl disabled:opacity-40 hover:opacity-85 transition-opacity shrink-0"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Player score controls */}
                {ranked.length > 0 && (
                  <div>
                    <p style={{ color: "#6b6b8a", letterSpacing: "0.1em" }} className="text-xs uppercase mb-3">Adjust scores</p>
                    <div className="space-y-2">
                      {ranked.map((p, i) => (
                        <div key={p.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }} className="rounded-xl px-4 py-3 flex items-center gap-3">
                          <span style={{ color: `${G}70` }} className="text-sm w-5 shrink-0">{i + 1}</span>
                          <p style={{ color: "#e2e8f0" }} className="flex-1 text-sm truncate">{p.name}</p>

                          <button
                            onClick={() => adjust(p.id, -stepSize)}
                            disabled={p.score === 0}
                            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#94a3b8" }}
                            className="w-8 h-8 rounded-lg text-base font-bold flex items-center justify-center disabled:opacity-30 hover:border-red-400 hover:text-red-400 transition-colors"
                          >
                            −
                          </button>

                          <input
                            type="number"
                            min="0"
                            value={p.score}
                            onChange={(e) => setScore(p.id, e.target.value)}
                            style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${G}40`, color: G, fontFamily: "'Playfair Display', serif", width: 64, textAlign: "center" }}
                            className="rounded-lg py-1 text-base font-bold focus:outline-none"
                          />

                          <button
                            onClick={() => adjust(p.id, stepSize)}
                            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#94a3b8" }}
                            className="w-8 h-8 rounded-lg text-base font-bold flex items-center justify-center hover:border-green-400 hover:text-green-400 transition-colors"
                          >
                            +
                          </button>

                          <button
                            onClick={() => removePlayer(p.id)}
                            style={{ color: "rgba(255,255,255,0.2)" }}
                            className="ml-1 hover:text-red-400 transition-colors text-lg leading-none"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
